import LegalLayout from "@/components/LegalLayout";
import { useLanguage } from "@/App";
import { Plane, Building, Car, Briefcase, Users, ShoppingBag, Bot, Heart } from "lucide-react";

export default function AboutUs() {
  const { language, t } = useLanguage();
  const ar = language === "ar";

  const pillars = [
    { i: Plane, ar: "استقبال المطار", en: "Airport Pickup", d_ar: "سائق عربي موثوق ينتظرك عند الوصول.", d_en: "A trusted Arab driver waiting on arrival." },
    { i: Building, ar: "السكن", en: "Housing", d_ar: "آلاف الشقق والغرف بتفاصيل دقيقة وأسعار شفافة.", d_en: "Thousands of apartments and rooms with transparent prices." },
    { i: Car, ar: "السيارات", en: "Vehicles", d_ar: "بيع وإيجار السيارات بشكل مباشر مع المالكين.", d_en: "Buy or rent vehicles directly from owners." },
    { i: Briefcase, ar: "العمل", en: "Jobs", d_ar: "بحث ذكي عن الوظائف بحسب الولاية والراتب.", d_en: "Smart job search by state and salary." },
    { i: Users, ar: "دليل الخدمات", en: "Services Directory", d_ar: "محامون، أطباء، صنايعية ومعلمون من المجتمع العربي.", d_en: "Lawyers, doctors, handymen, teachers from the Arab community." },
    { i: ShoppingBag, ar: "البقالة", en: "Grocery", d_ar: "متاجر عربية مع توصيل، أو قائمة تسوّق يشتريها سائقنا لك.", d_en: "Arab stores with delivery, or shopping lists fulfilled by our drivers." },
    { i: Bot, ar: "المساعد الذكي", en: "AI Assistant", d_ar: "إجابات فورية بالعربية على أسئلتك من بيانات Arab Sanad.", d_en: "Instant Arabic answers from Arab Sanad data." },
    { i: Heart, ar: "السياحة", en: "Tourism", d_ar: "اكتشف أشهر الأماكن في كل ولاية.", d_en: "Discover the best places in every state." },
  ];

  return (
    <LegalLayout titleAr="من نحن" titleEn="About Us">
      <section>
        <p className="text-lg">{ar
          ? "Arab Sanad (سند) هي المنصة العربية الأولى المخصصة للمغتربين العرب في الولايات المتحدة. مهمتنا أن نكون \"سندك من أول يوم\" — كل ما تحتاجه للاستقرار والازدهار في مكان واحد."
          : "Arab Sanad is the leading Arabic-first platform for Arab newcomers and expats in the United States. Our mission is to be your \"sanad\" — your support — from day one. Everything you need to settle and thrive in one place."}</p>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-white mb-3">{ar ? "قصتنا" : "Our Story"}</h2>
        <p>{ar
          ? "بدأنا Arab Sanad بعد أن لاحظنا أن كل مغترب عربي يعيد اختراع نفس العجلة: البحث عن سائق موثوق، شقة آمنة، طبيب يتحدث العربية، أو متجر يبيع المعكرونة والحلويات التي اعتاد عليها. أردنا بناء مكان واحد يجمع كل ذلك — بتصميم عربي جذّاب وأمان عالي."
          : "We started Arab Sanad after seeing every newcomer reinvent the same wheel: finding a trusted driver, a safe apartment, an Arabic-speaking doctor, or a store with the foods you grew up on. We wanted to build a single home for all of that — with a beautiful Arabic-first design and serious security."}</p>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-white mb-4">{ar ? "ماذا نقدّم" : "What We Offer"}</h2>
        <div className="grid sm:grid-cols-2 gap-3 not-prose">
          {pillars.map((p, i) => (
            <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                <p.i className="text-yellow-500" size={18} />
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">{ar ? p.ar : p.en}</h3>
                <p className="text-xs text-slate-400">{ar ? p.d_ar : p.d_en}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-white mb-3">{ar ? "قيمنا" : "Our Values"}</h2>
        <ul className="list-disc pr-6 space-y-1">
          <li><b>{ar ? "الثقة:" : "Trust:"}</b> {ar ? "نتحقق من السائقين ومقدمي الخدمات يدوياً." : "We manually verify drivers and service providers."}</li>
          <li><b>{ar ? "الكرامة:" : "Dignity:"}</b> {ar ? "كل عربي يستحق خدمة بلغته وثقافته." : "Every Arab deserves service in their language and culture."}</li>
          <li><b>{ar ? "الشفافية:" : "Transparency:"}</b> {ar ? "أسعار واضحة، تقييمات حقيقية، لا رسوم خفية." : "Clear prices, real reviews, no hidden fees."}</li>
          <li><b>{ar ? "الأمان:" : "Safety:"}</b> {ar ? "تشفير قوي، حماية ضد الاحتيال." : "Strong encryption, fraud protection."}</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-white mb-3">{ar ? "أين نخدم" : "Where We Serve"}</h2>
        <p>{ar
          ? "في جميع الولايات الأمريكية الخمسين. أنشط مدننا حالياً: نيويورك، نيوجيرسي، ميشيغان، تكساس، كاليفورنيا، فلوريدا."
          : "Across all 50 US states. Our most active cities today: New York, New Jersey, Michigan, Texas, California, Florida."}</p>
      </section>

      <section>
        <h2 className="text-2xl font-bold text-white mb-3">{ar ? "انضم إلينا" : "Join Us"}</h2>
        <p>{ar
          ? "نبحث دائماً عن سائقين، مقدّمي خدمات، وأصحاب متاجر يريدون خدمة الجالية. تواصل معنا عبر صفحة \"اتصل بنا\" أو أرسل إلى partners@arabsanad.com."
          : "We are always looking for drivers, service providers, and store owners who want to serve the community. Reach out via Contact or partners@arabsanad.com."}</p>
      </section>
    </LegalLayout>
  );
}
